
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import './MitekAgreement.css';
import { useWidthContext } from '../../providers/Width';
import { checkForPhoneNumber, redirect, replaceWithCustomerNumber } from '../../providers/MitekProvider/MitekUtils';
import { checkVideoMediaDevicesDisabled } from '../../utils/cob-utils';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import SessionStorage from 'utils/SessionStorage';
import { acquisitionApiRequest } from 'utils/acquisition-api/AcquisitionApi';
import { InitializeAcquisitionApiResponse } from 'types/responses/InitializeAcquisitionApi';
import { Button, Row, Col, Title, Image, Container } from '../../importCL';

interface IMitekAgreementProps extends MappedComponentProperties {
    usingModernApi?: boolean,
    imgPath?: string, 
    imgAltText?: string, 
    mainTitle?: string, 

    description?: string, 
    terms?: string, 
    disclaimer?: string, 
    altDisclaimer?: string,
    ctaButtonText?: string,
    ctaButtonLinkDesktop?: string, 
    ctaButtonLinkMobile?: string,

    mobileTerms?: string, 
    mobileDisclaimer?: string,

    usingHardReset?: boolean,
    desktopFlowAvailable?: boolean,

    checkingCameraPermissions?: boolean,
    mediaPermissionsError?: string,
}

export const MitekAgreementEditConfig = {
    emptyLabel: 'Mitek Agreement',
    isEmpty: (props: PropsWithChildren<IMitekAgreementProps>) => {
        return !props || !props.mainTitle;
    },
};

const MitekAgreement = (props: PropsWithChildren<IMitekAgreementProps>): JSX.Element => {
    const { width } = useWidthContext();
    const isMobile = width <= 768 || window.location.search.includes('sid');

    const { showPushdown, closePushdown } = usePushdownContext();
    const [ isCameraReady, setIsCameraReady ] = useState<boolean>(!isMobile);
    const [ isApiReady, setIsApiReady ] = useState<boolean>(SessionStorage.verifyNotEmpty('initResponse'));
    const [ applicantPhoneNumber, setApplicantPhoneNumber ] = useState();

    const handleClick = () => {
        if (props.desktopFlowAvailable) {
            redirect(isMobile ? props.ctaButtonLinkMobile! : props.ctaButtonLinkDesktop!);
        } else {
            redirect(props.ctaButtonLinkMobile!);
        }
    };

    useEffect(() => {
        // Initialising Acquisitions API in the case of Desktop + Mobile Flow
        if (
            SessionStorage.verifyNotEmpty('initResponse') 
            || window.location.href.includes('wcmmode=disabled')
            || window.location.href.includes('editor.html')
        ) {
            return;
        }

        if(!props.usingModernApi) {
            acquisitionApiRequest<InitializeAcquisitionApiResponse>(
                'init-acquisition-api',
                {},
            ).then((response) => {
                if (response?.data) {
                    SessionStorage.setObject('initResponse', response.data);
                    setIsApiReady(true);
                }
            });
        } else {
            setIsApiReady(true);
        }

        checkForPhoneNumber().then(setApplicantPhoneNumber);
    }, []);

    useEffect(() => {
        if (!isMobile || !props.checkingCameraPermissions) {
            setIsCameraReady(true);
            return;
        }

        const handleMediaCallback = (isDisabled: boolean) => {
            setIsCameraReady(!isDisabled);

            if (isDisabled) {
                showPushdown(props.mediaPermissionsError as string);
            }
        };

        const retrievePhone = async () => {
            setApplicantPhoneNumber(await checkForPhoneNumber());
        };

        retrievePhone();

        closePushdown();

        checkVideoMediaDevicesDisabled(handleMediaCallback);

    }, []);

    return (
        <Container id='mitek-agreement' className='MitekAgreement__container'>
            <Row>
                <Col>
                    {props.imgPath && 
                    <Image 
                        loading='lazy'
                        className='MitekAgreement__mainImage'
                        src={props.imgPath}
                        alt={props.imgAltText} />
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    {props.mainTitle &&
                    <Title className={`MitekAgreement__title${isMobile ? 'Mobile' : ''}`} tag='h1'>{transformHTMLParserLinks(props.mainTitle)}</Title>
                    }
                </Col>
            </Row>
            <Row> 
                <Col>
                    <div className='MitekAgreement__description'>{props.description && transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.description))}</div>
                    {isMobile ?
                        <div className='MitekAgreement__mobileTerms'>{props.mobileTerms && transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.mobileTerms))}</div>
                        :
                        <div className='MitekAgreement__terms'>{props.terms && transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.terms))}</div>
                    }
                </Col>
            </Row>
            <Row className='MitekAgreement__disclaimerRow'>
                <Col className='MitekAgreement__disclaimerCol'>
                    {isMobile ? 
                        <div className='MitekAgreement__mobileDisclaimer'>{transformHTMLParserLinks(props.mobileDisclaimer)}</div>
                        :
                        <div className='MitekAgreement__disclaimer'>{props.disclaimer && transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.disclaimer, props.altDisclaimer))}</div>
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    <div>
                        <Button
                            disableOnClick
                            disabled={!isCameraReady || !isApiReady}
                            aria-disabled={!isCameraReady || !isApiReady}
                            onClick={handleClick}
                            className='MitekAgreement__button'
                            size='medium'>
                                {props.ctaButtonText}
                        </Button>
                    </div>                    
                </Col>
            </Row>
        </Container>);
};

export default MitekAgreement;
